import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useTranslation } from "react-i18next";
import SaveIcon from "@mui/icons-material/Save";
import Grid from "@mui/material/Grid";
import EnvironmentPollutionConfigSlider from "./EnvironmentPollutionConfigSlider";
import {
  EnvironmentPollution,
  VisualSensorSensitivity,
} from "../../../../interfaces/robot/cleaning-configurations/environment-pollution";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import ReadOnlyProtectedButton from "../../../Inputs/ReadOnlyProtectedButton";
import {
  selectCustomEnvironmentPollution,
  selectEnvironmentPollutionRestState,
  selectGlobalMapRulesConfiguration,
  selectRobotRswSoftwareVersion,
} from "../../../../store/robot-configurations/robot-configurations-selectors";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import DialogWrapperWithClose from "../../../General/DialogWrapperWithClose";
import OptionSwitchInput from "../../../../components/Inputs/OptionSwitchInput";
import CustomSnackbar from "../../../Feedback/CustomSnackbar";
import RobotVersionControlledWrapper from "../../../misc/RobotVersionControlledWrapper";
import { isVersionAGreaterThanB } from "../../../../utils/version-util";

const CustomEnvironmentPollution: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const {
    updateCustomEnvironmentPollutionConfiguration,
    getEnvironmentPollution,
    patchCustomEnvironmentPollution,
    patchEnvironmentPollutionRestSet,
    patchParamList,
  } = useSharedActions();

  const customEnvironmentPollution = useSharedSelector((state) => selectCustomEnvironmentPollution(state));
  const environmentPollutionRestState = useSharedSelector((state) => selectEnvironmentPollutionRestState(state));
  const globalMapRulesConfiguration = useSharedSelector((state) => selectGlobalMapRulesConfiguration(state));
  const robotVersion = useSharedSelector((state) => selectRobotRswSoftwareVersion(state));

  const [legacyDrivingSpeed, setLegacyDrivingSpeed] = useState(globalMapRulesConfiguration?.drivingSpeed ?? 100);

  useEffect(() => {
    if (globalMapRulesConfiguration?.drivingSpeed != null) {
      setLegacyDrivingSpeed(globalMapRulesConfiguration.drivingSpeed);
    }
  }, [globalMapRulesConfiguration?.drivingSpeed]);

  useEffect(() => {
    if (environmentPollutionRestState.getStatus === HttpFetchStatus.Idle) {
      dispatch(getEnvironmentPollution());
    }
  }, [dispatch, getEnvironmentPollution, environmentPollutionRestState.getStatus]);

  const closeHandler = () => {
    dispatch(getEnvironmentPollution());
    onClose();
  };

  const saveClickHandler = () => {
    if (customEnvironmentPollution != null) {
      dispatch(patchCustomEnvironmentPollution(customEnvironmentPollution));
    }
    if (
      robotVersion != null &&
      isVersionAGreaterThanB("2026.1.0", robotVersion) &&
      globalMapRulesConfiguration != null
    ) {
      dispatch(
        patchParamList({
          mapRulesConfiguration: {
            ...globalMapRulesConfiguration,
            drivingSpeed: legacyDrivingSpeed,
          },
        }),
      );
    }
    onClose();
  };

  const handleFrontBrushSpeedChange = (value: number) => {
    dispatch(
      updateCustomEnvironmentPollutionConfiguration({
        ...customEnvironmentPollution,
        frontBrushSpeed: value,
      } as EnvironmentPollution),
    );
  };

  const handleRollerBrushSpeedChange = (value: number) => {
    dispatch(
      updateCustomEnvironmentPollutionConfiguration({
        ...customEnvironmentPollution,
        rollerBrushSpeed: value,
      } as EnvironmentPollution),
    );
  };

  const handleAspirationSpeedChange = (value: number) => {
    dispatch(
      updateCustomEnvironmentPollutionConfiguration({
        ...customEnvironmentPollution,
        aspirationSpeed: value,
      } as EnvironmentPollution),
    );
  };

  const handleCleaningDrivingSpeedChange = (value: number) => {
    if (customEnvironmentPollution != null) {
      dispatch(
        updateCustomEnvironmentPollutionConfiguration({
          ...customEnvironmentPollution,
          cleaningDrivingSpeed: Math.min(value, legacyDrivingSpeed),
        } as EnvironmentPollution),
      );
    }
  };

  const handleBrushesBwPeriod = (value: number) => {
    dispatch(
      updateCustomEnvironmentPollutionConfiguration({
        ...customEnvironmentPollution,
        brushesBwPeriod: value,
      } as EnvironmentPollution),
    );
  };

  const handleLidarSensitivityChange = (value: string) => {
    dispatch(
      updateCustomEnvironmentPollutionConfiguration({
        ...customEnvironmentPollution,
        lidarSensitivity: value as VisualSensorSensitivity,
      } as EnvironmentPollution),
    );
  };

  const handleCameraSensitivityChange = (value: string) => {
    dispatch(
      updateCustomEnvironmentPollutionConfiguration({
        ...customEnvironmentPollution,
        cameraSensitivity: value as VisualSensorSensitivity,
      } as EnvironmentPollution),
    );
  };

  const handleDrivingSpeedChange = (value: number) => {
    // legacy 26.1 for custom config
    setLegacyDrivingSpeed(value);
    if (customEnvironmentPollution?.cleaningDrivingSpeed != null) {
      dispatch(
        updateCustomEnvironmentPollutionConfiguration({
          ...customEnvironmentPollution,
          cleaningDrivingSpeed: Math.min(value, customEnvironmentPollution.cleaningDrivingSpeed),
        } as EnvironmentPollution),
      );
    }
  };

  const resetEnvironmentPollutionRestState = () => {
    dispatch(patchEnvironmentPollutionRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  if (customEnvironmentPollution == null || globalMapRulesConfiguration == null) {
    return null;
  }

  return (
    <React.Fragment>
      {customEnvironmentPollution != null && (
        <DialogWrapperWithClose
          open={isOpen}
          onClose={closeHandler}
          fullWidth
          maxWidth="sm"
          title={t("configuration-cleaning-environmentPollution-custom")}
        >
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <EnvironmentPollutionConfigSlider
                  value={customEnvironmentPollution.frontBrushSpeed}
                  inputLabel="configuration-cleaning-EP-frontBrushSpeed"
                  onChange={handleFrontBrushSpeedChange}
                />
              </Grid>
              <Grid item xs={12}>
                <EnvironmentPollutionConfigSlider
                  value={customEnvironmentPollution.rollerBrushSpeed}
                  inputLabel="configuration-cleaning-EP-rollerBrushSpeed"
                  onChange={handleRollerBrushSpeedChange}
                />
                {customEnvironmentPollution.rollerBrushSpeed < 70 && (
                  <Alert severity="warning">{t("configuration-cleaning-EP-rollerBrushSpeed-low-warning")}</Alert>
                )}
              </Grid>
              <Grid item xs={12}>
                <EnvironmentPollutionConfigSlider
                  value={customEnvironmentPollution.aspirationSpeed}
                  inputLabel="configuration-cleaning-EP-aspirationSpeed"
                  onChange={handleAspirationSpeedChange}
                />
              </Grid>
              <RobotVersionControlledWrapper maxVersion="2026.1.0">
                <Grid item xs={12}>
                  <EnvironmentPollutionConfigSlider
                    value={legacyDrivingSpeed}
                    inputLabel="configuration-cleaning-EP-drivingSpeed"
                    onChange={handleDrivingSpeedChange}
                  />
                </Grid>
              </RobotVersionControlledWrapper>
              <Grid item xs={12}>
                <EnvironmentPollutionConfigSlider
                  value={customEnvironmentPollution.cleaningDrivingSpeed}
                  inputLabel="configuration-cleaning-EP-cleaningDrivingSpeed"
                  onChange={handleCleaningDrivingSpeedChange}
                  maxRange={legacyDrivingSpeed}
                  infoTooltip={t("configuration-cleaning-EP-cleaningDrivingSpeed-info-tooltip")}
                />
              </Grid>
              <Grid item xs={12}>
                <EnvironmentPollutionConfigSlider
                  value={customEnvironmentPollution.brushesBwPeriod}
                  inputLabel="configuration-cleaning-EP-brushesBwPeriod"
                  onChange={handleBrushesBwPeriod}
                  timeIcon
                  maxRange={120}
                />
              </Grid>
              <Grid item xs={12}>
                <OptionSwitchInput
                  label={t("configuration-cleaning-EP-lidarSensitivity")}
                  options={Object.values(VisualSensorSensitivity).map((label) => ({
                    label: t(label),
                    value: label,
                  }))}
                  value={customEnvironmentPollution.lidarSensitivity}
                  onChange={handleLidarSensitivityChange}
                />
              </Grid>
              <Grid item xs={12}>
                <OptionSwitchInput
                  label={t("configuration-cleaning-EP-cameraSensitivity")}
                  options={Object.values(VisualSensorSensitivity).map((label) => ({
                    label: t(label),
                    value: label,
                  }))}
                  value={customEnvironmentPollution.cameraSensitivity}
                  onChange={handleCameraSensitivityChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ReadOnlyProtectedButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={saveClickHandler}
            >
              {t("button-save")}
            </ReadOnlyProtectedButton>
          </DialogActions>
        </DialogWrapperWithClose>
      )}
      <CustomSnackbar
        title={t("success-saving-custom-environmentpollution")}
        open={environmentPollutionRestState.patchStatus === HttpFetchStatus.Success}
        severity="success"
        onClose={resetEnvironmentPollutionRestState}
      />
      <CustomSnackbar
        title={t("error-saving-custom-environmentpollution")}
        open={environmentPollutionRestState.patchStatus === HttpFetchStatus.Failed}
        severity="error"
        onClose={resetEnvironmentPollutionRestState}
      />
    </React.Fragment>
  );
};

export default CustomEnvironmentPollution;
